/**
 * PromptEditor - CodeMirror-based prompt template editor that highlights
 * recognized {variable} tokens inline and makes them clickable.
 */

import React, { useEffect, useRef } from 'react';
import {
  EditorState,
  StateField,
  StateEffect,
  Range,
} from '@codemirror/state';
import {
  EditorView,
  Decoration,
  DecorationSet,
  keymap,
  placeholder as placeholderExtension,
} from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';

const VARIABLE_RE = /\{(\w+)\}/g;

interface PromptEditorProps {
  value: string;
  onChange: (text: string) => void;
  recognizedNames: Set<string>;
  // Names with a configured data source - unconfigured ones are styled as needing attention.
  configuredNames?: Set<string>;
  onVariableClick: (name: string, anchor: DOMRect) => void;
  placeholder?: string;
}

// Holds the current set of recognized variable names inside editor state, so
// the decoration StateField can react to it without recreating the editor.
interface VariableNames {
  recognized: Set<string>;
  configured: Set<string> | null;
}

const setRecognizedNames = StateEffect.define<VariableNames>();
const recognizedNamesField = StateField.define<VariableNames>({
  create: () => ({ recognized: new Set(), configured: null }),
  update: (value, tr) => {
    for (const effect of tr.effects) {
      if (effect.is(setRecognizedNames)) return effect.value;
    }
    return value;
  },
});

function buildDecorations(doc: string, names: VariableNames): DecorationSet {
  const ranges: Range<Decoration>[] = [];
  for (const match of doc.matchAll(VARIABLE_RE)) {
    const name = match[1];
    if (!names.recognized.has(name) || match.index === undefined) continue;
    const from = match.index;
    const to = from + match[0].length;
    const unconfigured = names.configured !== null && !names.configured.has(name);
    ranges.push(
      Decoration.mark({
        class: unconfigured ? 'cm-variable-token cm-variable-token--unconfigured' : 'cm-variable-token',
        attributes: {
          'data-variable-name': name,
          title: unconfigured ? 'Click to choose where this comes from' : 'Click to edit',
        },
      }).range(from, to)
    );
  }
  return Decoration.set(ranges);
}

const variableDecorationsField = StateField.define<DecorationSet>({
  create: (state) => buildDecorations(state.doc.toString(), state.field(recognizedNamesField)),
  update: (decorations, tr) => {
    if (!tr.docChanged && !tr.effects.some((e) => e.is(setRecognizedNames))) {
      return decorations;
    }
    return buildDecorations(tr.state.doc.toString(), tr.state.field(recognizedNamesField));
  },
  provide: (field) => EditorView.decorations.from(field),
});

export const PromptEditor: React.FC<PromptEditorProps> = ({
  value,
  onChange,
  recognizedNames,
  configuredNames,
  onVariableClick,
  placeholder,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  // Avoids feeding a change back into itself when the update came from our
  // own onChange -> parent state -> value prop round trip.
  const lastEmittedValue = useRef(value);
  const onChangeRef = useRef(onChange);
  const onVariableClickRef = useRef(onVariableClick);
  onChangeRef.current = onChange;
  onVariableClickRef.current = onVariableClick;

  useEffect(() => {
    if (!containerRef.current) return;

    const state = EditorState.create({
      doc: value,
      extensions: [
        history(),
        keymap.of([...defaultKeymap, ...historyKeymap]),
        EditorView.lineWrapping,
        placeholderExtension(placeholder || ''),
        recognizedNamesField,
        variableDecorationsField,
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            const text = update.state.doc.toString();
            lastEmittedValue.current = text;
            onChangeRef.current(text);
          }
        }),
        EditorView.domEventHandlers({
          mousedown(event, view) {
            const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
            if (pos == null) return false;
            const decorations = view.state.field(variableDecorationsField);
            let clicked: { name: string; from: number; to: number } | null = null;
            decorations.between(pos, pos, (from, to, deco) => {
              const name = (deco.spec.attributes as Record<string, string>)?.['data-variable-name'];
              if (pos >= from && pos <= to && name) clicked = { name, from, to };
            });
            if (clicked) {
              const { name, from, to } = clicked;
              const start = view.coordsAtPos(from);
              const end = view.coordsAtPos(to, -1);
              const anchor = start && end
                ? new DOMRect(start.left, start.top, Math.max(end.right - start.left, 0), start.bottom - start.top)
                : new DOMRect(event.clientX, event.clientY, 0, 0);
              onVariableClickRef.current(name, anchor);
              return true;
            }
            return false;
          },
        }),
        EditorView.theme({
          '&': {
            fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
            fontSize: '0.8125rem',
            border: '1px solid var(--border-color)',
            borderRadius: '10px',
            background: 'white',
          },
          '&.cm-focused': {
            outline: 'none',
            borderColor: 'var(--primary-color)',
            boxShadow: '0 0 0 3px rgba(var(--primary-color-rgb), 0.1)',
          },
          '.cm-content': {
            padding: 'var(--spacing-sm) var(--spacing-md)',
            minHeight: '160px',
          },
          '.cm-scroller': {
            overflow: 'auto',
          },
        }),
      ],
    });

    const view = new EditorView({ state, parent: containerRef.current });
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Editor is created once; value/placeholder changes are handled by the
    // sync effects below rather than recreating the editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes (e.g. loading an existing agent) into the editor.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (value === lastEmittedValue.current) return;
    const currentText = view.state.doc.toString();
    if (currentText === value) return;
    view.dispatch({
      changes: { from: 0, to: currentText.length, insert: value },
    });
    lastEmittedValue.current = value;
  }, [value]);

  // Sync the recognized/configured name sets so decorations stay live as fields change.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: setRecognizedNames.of({ recognized: recognizedNames, configured: configuredNames ?? null }),
    });
  }, [recognizedNames, configuredNames]);

  return <div ref={containerRef} className="prompt-editor" />;
};
